import type { HttpTransport, TransportResponse } from '@mangovoice/api-client'
import { type ExtensionType, MessageGroup } from '@mangovoice/types'

export type { MessageGroup }

// ---------------------------------------------------------------------------
// API response types (backed by @mangovoice/api-client TransportResponse)
// ---------------------------------------------------------------------------

export interface ApiError {
  type?: string
  code?: string
  message?: string
  errors?: Array<{
    code: string
    detail: string
    attr: string | null
  }>
}

export type ApiResponse<T, _E = unknown> = TransportResponse<T>

export type MessagingApiClient = Pick<
  HttpTransport,
  'get' | 'post' | 'patch' | 'put' | 'delete'
>

export interface PaginatedApiResult<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

// ---------------------------------------------------------------------------
// Domain types
// ---------------------------------------------------------------------------

/** Write payload for creating/updating a message group */
export interface MessageGroupWritePayload {
  name: string
  pbx_id?: number
  primary_phone_number_id?: number
  secondary_phone_number_ids?: number[]
}

/** Phone number as returned by /phone-numbers/ */
export interface PhoneNumberDetail {
  id: number
  pbx_id: number
  name: string
  number: string
  sms_activated_at: string | null
  campaign_id?: string | null
  local_presence?: number
  sms_information?: {
    relay?: boolean
    group_name?: string
  } | null
}

/**
 * Returns true when a phone number has a valid campaign registration.
 * Treats `undefined` (field not in API response) as valid so the UI
 * degrades gracefully until the backend exposes the field.
 * Accepts any object with an optional campaign_id (PhoneNumber or PhoneNumberDetail).
 */
export const hasValidCampaignId = (phone: {
  campaign_id?: string | null
}): boolean => phone.campaign_id === undefined || !!phone.campaign_id

// ---------------------------------------------------------------------------
// Pagination & sorting
// ---------------------------------------------------------------------------

export interface MessageGroupListParams {
  limit?: number
  offset?: number
  ordering?: string
  search?: string
}

export interface MessageGroupListResult {
  count: number
  next: string | null
  previous: string | null
  results: MessageGroup[]
}

// ---------------------------------------------------------------------------
// Member types
// ---------------------------------------------------------------------------

export interface MessageGroupMember {
  id: number
  extension_id: number
  group_id: number
  extension_name: string
  extension_number: string
  created_at: string
  updated_at: string
}

export interface ExtensionSummary {
  id: number
  name: string
  number: string
  type: ExtensionType
  pbx_id: number
  // The Extension model exposes `active` as a Django `BooleanField`, so the
  // `/app/v1/extensions/` serializer surfaces it as a true JSON boolean —
  // not the integer `is_active` that other internal code paths use. Used
  // to filter inactive extensions out of "Available Members"; see
  // `MessagingDetailPage.availableMembers` for the defensive check.
  active?: boolean
}

// ---------------------------------------------------------------------------
// Messaging API interface
// ---------------------------------------------------------------------------

export interface MessagingApi {
  // Real API (backed by endpoints)
  getMessageGroups(
    pbxId: number,
    params?: MessageGroupListParams,
  ): Promise<MessageGroupListResult>
  getMessageGroup(groupId: number): Promise<MessageGroup>
  createMessageGroup(payload: MessageGroupWritePayload): Promise<MessageGroup>
  updateMessageGroup(
    groupId: number,
    payload: Partial<MessageGroupWritePayload>,
  ): Promise<MessageGroup>
  deleteMessageGroup(groupId: number): Promise<void>
  getAvailableSmsNumbers(pbxId: number): Promise<PhoneNumberDetail[]>

  getMessageGroupMembers(groupId: number): Promise<MessageGroupMember[]>
  getExtensions(pbxId: number): Promise<ExtensionSummary[]>
  addMessageGroupMember(
    groupId: number,
    extensionId: number,
  ): Promise<MessageGroupMember>
  removeMessageGroupMember(memberId: number): Promise<void>
}
